import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../home/components/Navigation';
import {useApiClient} from "../../base/hooks/useApiClient";

export function ResetPasswordPage() {
    const [email, setEmail] = useState('');
    const [formError, setFormError] = useState<string | undefined>();
    const [successMessage, setSuccessMessage] = useState<string | undefined>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const apiClient = useApiClient();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);

        const response = await apiClient.post('reset-password', { email });

        if (!response.ok) {
            const { error } = await response.json();
            setSuccessMessage(undefined);
            setFormError(error);
            setIsLoading(false);
            return;
        }

        setSuccessMessage('Password reset link has been sent to your email.');
        setFormError(undefined);
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 py-12 px-4 sm:px-6 lg:px-8">
            <button
                onClick={() => navigate('/')}
                className="absolute top-4 left-4 flex items-center text-white hover:text-indigo-100 transition-colors"
            >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
            </button>

            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
                    <p className="text-gray-600">Enter your email to reset your password</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {formError && (
                        <p className="text-sm text-red-600 mb-2">
                            {formError}
                        </p>
                    )}
                    {successMessage && (
                        <p className="text-sm text-green-600 mb-2">
                            {successMessage}
                        </p>
                    )}

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="your@email.com"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <Button primary className="w-full" disabled={isLoading}>
                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-600">
                    Remembered your password?{' '}
                    <button
                        onClick={() => navigate('/login')}
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                        Sign in
                    </button>
                </p>
            </div>
        </div>
    );
}
